<template>
  <v-app-bar
    v-if="$route.name !== 'login'"
    app
    color="white"
    clipped-right
    clipped-left
    flat
    height="64"
    class="custom-app-bar"
  >
    <!-- Logo -->
    <v-hover v-slot="{ hover }">
      <div 
        class="logo-container d-flex align-center mr-4" 
        :class="{ 'logo-hover': hover }"
        @click="goToPage('/')"
      >
        <v-img
          contain
          max-height="42"
          max-width="110"
          src="@/assets/complete_logo.png"
        />
      </div>
    </v-hover>

    <v-divider vertical inset class="my-auto mr-3 hidden-sm-and-down" style="height: 28px;"></v-divider>

    <!-- Menú móvil (pantallas pequeñas) -->
    <v-menu 
      v-if="$vuetify.breakpoint.smAndDown" 
      offset-y 
      transition="slide-y-transition"
      content-class="elevation-6 rounded-lg"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn icon v-bind="attrs" v-on="on" class="menu-btn-mobile">
          <v-icon color="grey darken-2">mdi-menu</v-icon>
        </v-btn>
      </template>

      <v-list dense class="py-2" width="220">
        <v-list-item
          v-for="item in menuItems"
          :key="item.routeName"
          :to="{ name: item.routeName }"
          exact
          active-class="primary--text rounded-lg font-weight-bold"
          class="mx-2 mb-1"
        >
          <v-list-item-icon class="mr-3">
            <v-icon small :color="$route.name === item.routeName ? 'primary' : 'grey darken-1'">
              {{ item.icon }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title class="text-capitalize">{{ item.text }}</v-list-item-title>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <v-list-item class="mx-2 rounded-lg error--text" @click="logoutFromApp">
          <v-list-item-icon class="mr-3">
            <v-icon small color="error">mdi-logout</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="font-weight-medium">Cerrar Sesión</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Pestañas Desktop -->
    <v-tabs 
      v-else 
      optional 
      color="primary" 
      class="ml-1 custom-tabs" 
      slider-size="3"
    >
      <v-tab
        v-for="item in menuItems"
        :key="item.routeName"
        :to="{ name: item.routeName }"
        class="nav-tab text-none font-weight-medium text-caption"
        :exact="item.routeName !== 'mantenimiento'"
      >
        <v-icon left small class="mr-1">{{ item.icon }}</v-icon>
        {{ item.text }}
      </v-tab>
    </v-tabs>

    <v-spacer></v-spacer>

    <!-- Acciones barra derecha -->
    <div class="d-flex align-center actions-wrapper">
      
      <!-- Botón: Cambiar Contraseña -->
      <v-tooltip v-if="!bloqueoQR" bottom transition="fade-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" @click="openPasswordDialog = true" class="action-btn mx-1">
            <v-icon small color="grey darken-2">mdi-lock-reset</v-icon>
          </v-btn>
        </template>
        <span>Cambiar contraseña</span>
      </v-tooltip>
      
      <!-- Botón: Administrar usuarios -->
      <v-tooltip v-if="puedeAdministrarUsuarios && !bloqueoQR" bottom transition="fade-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" @click="goToPage('register')" class="action-btn mx-1">
            <v-icon small color="grey darken-2">mdi-account-cog-outline</v-icon>
          </v-btn>
        </template>
        <span>Administrar usuarios</span>
      </v-tooltip>

      <!-- Botón: Cesta -->
      <v-tooltip v-if="rolPermitido('articles') && !bloqueoQR" bottom transition="fade-transition">
        <template v-slot:activator="{ on: onTooltip }">
          <v-menu offset-y left min-width="340" max-width="380" :close-on-content-click="false" transition="slide-y-transition">
            <template v-slot:activator="{ on: onMenu }">
              <v-badge 
                bordered 
                color="error" 
                overlap 
                :content="badgeContent" 
                :value="badgeContent > 0" 
                class="mx-2"
              >
                <v-btn icon v-on="{ ...onTooltip, ...onMenu }" class="action-btn">
                  <v-icon small :color="badgeContent > 0 ? 'primary' : 'grey darken-2'">mdi-cart-outline</v-icon>
                </v-btn>
              </v-badge>
            </template>

            <!-- Card de Carrito Desplegable -->
            <v-card elevation="8" class="rounded-xl overflow-hidden">
              <v-toolbar color="primary" dark dense flat class="px-2">
                <v-icon small left>mdi-cart</v-icon>
                <v-toolbar-title class="subtitle-2 font-weight-bold">Mi Carrito</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-chip x-small color="white" text-color="primary" class="font-weight-bold">
                  {{ badgeContent }} ítems
                </v-chip>
              </v-toolbar>

              <v-list dense max-height="340" class="overflow-y-auto pa-2">
                <div v-if="articlesInCart().length === 0" class="text-center py-6">
                  <v-icon size="42" color="grey lighten-2">mdi-cart-off</v-icon>
                  <p class="caption grey--text text--darken-1 mt-2 mb-0">Tu cesta está vacía</p>
                </div>

                <v-list-item 
                  v-for="item in articlesInCart()" 
                  :key="item.id"
                  class="rounded-lg mb-1 grey lighten-5"
                >
                  <v-list-item-avatar tile size="40" class="rounded">
                    <v-img :src="item.image_1920 || ''">
                      <template v-slot:placeholder>
                        <v-icon color="grey lighten-2">mdi-package-variant</v-icon>
                      </template>
                    </v-img>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="body-2 font-weight-medium text-truncate" style="max-width: 170px;">
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption grey--text">
                      Cantidad: <strong class="primary--text">{{ item.quantity }}</strong>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn icon x-small @click.stop="removeFromCart(item)">
                      <v-icon color="grey lighten-1" class="hover-red">mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <v-divider></v-divider>

              <v-card-actions v-if="articlesInCart().length > 0" class="pa-3">
                <v-btn block color="primary" depressed rounded @click="goToPage('shopping-cart')">
                  Ver Carrito Completo
                  <v-icon right small>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </template>
        <span>Ver Cesta</span>
      </v-tooltip>

      <v-divider vertical inset class="my-auto mx-2 hidden-sm-and-down" style="height: 24px;"></v-divider>

      <!-- Botón: Cerrar sesión -->
      <v-tooltip v-if="!$vuetify.breakpoint.smAndDown" bottom transition="fade-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" @click="logoutFromApp" class="action-btn logout-btn mx-1">
            <v-icon small color="error">mdi-power</v-icon>
          </v-btn>
        </template>
        <span>Cerrar Sesión</span>
      </v-tooltip>
    </div>

    <!-- Dialog para Cambiar Contraseña -->
    <v-dialog v-model="openPasswordDialog" max-width="420px" transition="dialog-bottom-transition">
      <v-card elevation="16" class="rounded-xl overflow-hidden">
        <v-toolbar color="primary" dark flat dense>
          <v-icon small class="mr-2">mdi-lock-reset</v-icon>
          <v-toolbar-title class="subtitle-1 font-weight-medium">Actualizar Contraseña</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="openPasswordDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
  
        <v-card-text class="pt-6 pb-2">
          <v-form ref="passwordForm" v-model="isFormValid" lazy-validation>
            
            <v-text-field
              id="password"
              name="psw"
              v-model="passwordData.oldPassword"
              label="Contraseña Actual"
              :append-icon="showPass1 ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPass1 ? 'text' : 'password'"
              @click:append="showPass1 = !showPass1"
              outlined
              dense
              prepend-inner-icon="mdi-lock-outline"
              :rules="[v => !!v || 'La contraseña actual es obligatoria']"
            ></v-text-field>
  
            <v-divider class="my-3"></v-divider>
  
            <v-text-field
              v-model="passwordData.newPassword"
              label="Nueva Contraseña"
              :append-icon="showPass2 ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPass2 ? 'text' : 'password'"
              @click:append="showPass2 = !showPass2"
              outlined
              dense
              prepend-inner-icon="mdi-lock-plus-outline"
            ></v-text-field>
  
            <v-text-field
              v-model="passwordData.confirmPassword"
              label="Confirmar Nueva Contraseña"
              :type="showPass2 ? 'text' : 'password'"
              outlined
              dense
              prepend-inner-icon="mdi-lock-check-outline"
              :rules="[
                v => !!v || 'Confirme su contraseña',
                v => v === passwordData.newPassword || 'Las contraseñas no coinciden'
              ]"
            ></v-text-field>
          </v-form>
        </v-card-text>
  
        <v-card-actions class="pb-5 px-5">
          <v-btn text rounded color="grey darken-1" @click="closePasswordDialog">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            depressed
            rounded
            :disabled="!isFormValid" 
            :loading="loading"
            @click="updatePassword"
            class="px-5 font-weight-medium"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app-bar>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "SuperiorMenu",
  data() {
    return {
      openPasswordDialog: false,
      isFormValid: false,
      loading: false,
      showPass1: false,
      showPass2: false,
      passwordData: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
    };
  },
  computed: {
    ...mapState(["esRamos", "permisos", "bloqueoQR"]),

    menuItems() {
      const items = [];
      // acceso por QR: sin navegación por la app
      if (this.bloqueoQR) return items;
      if (this.rolPermitido('partes')) {
        items.push({ text: 'Partes', routeName: 'home', icon: 'mdi-file-document-outline' });
      }
      if (this.rolPermitido('articles')) {
        items.push({ text: 'Tienda', routeName: 'articles', icon: 'mdi-shopping-outline' });
      }
      if (this.rolPermitido('cabinets')) {
        items.push({ text: 'Armarios', routeName: 'cabinets', icon: 'mdi-locker' });
      }
      if (this.rolPermitido('mantenimiento')) {
        items.push({ text: 'Mantenimiento', routeName: 'mantenimiento', icon: 'mdi-wrench-outline' });
      }
      if (this.rolPermitido('piezas')) {
        items.push({ text: 'Artículos', routeName: 'piezas', icon: 'mdi-cube-outline' });
      }
      return items;
    },

    puedeAdministrarUsuarios() {
      return this.permisos.includes('admin-users');
    },

    badgeContent() {
      return this.articlesInCart().length;
    },
  },
  methods: {
    ...mapGetters(["articlesInCart"]),
    ...mapActions(["postDataWithReturn"]),

    closePasswordDialog() {
      this.openPasswordDialog = false;
      this.passwordData = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
    },
    async updatePassword() {
      const isValid = this.$refs.passwordForm.validate();
      if (isValid) {
        this.loading = true;
        const payload = { ...this.passwordData, token: localStorage.getItem("token") };

        try {
          await this.postDataWithReturn({ path: "django/portal/change-password/", body: payload });
          this.closePasswordDialog();
        } catch (error) {
          console.error(error);
          this.$avisar("Error al cambiar contraseña", "error");
        } finally {
          this.loading = false;
        }
      }
    },
    
    rolPermitido(rolRequerido) {
      return this.permisos.includes(rolRequerido);
    },

    goToPage(path) {
      const finalPath = path === "/" ? "/" : `/${path}`;
      if (this.$route.path !== finalPath) {
        this.$router.push(finalPath).catch(() => {});
      }
    },

    removeFromCart(item) {
      item.in_cart = false;
    },

    logoutFromApp() {
      this.$store.dispatch("logout");
      this.$router.push("/login").catch(() => {});
    },
  }
};
</script>

<style scoped>
.custom-app-bar {
  border-bottom: 1px solid #edf2f7 !important;
  transition: all 0.3s ease;
}

.logo-container {
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.logo-hover {
  opacity: 0.85;
  transform: scale(0.98);
}

/* Estilización moderna de pestañas */
.custom-tabs {
  height: 48px;
}

.nav-tab {
  letter-spacing: 0.3px;
  min-width: 90px;
  transition: all 0.2s ease;
  border-radius: 8px 8px 0 0;
}

.nav-tab:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Botones de acción derecha */
.action-btn {
  background: #f8fafc;
  transition: all 0.2s ease-in-out;
}

.action-btn:hover {
  background: #f1f5f9;
  transform: translateY(-1px);
}

.logout-btn:hover {
  background: #fef2f2;
}

.hover-red:hover {
  color: #ef4444 !important;
}
</style>